import { degrees, radians } from '../../../../Math.js';
import { ShadowController, SHADOW_DEFAULTS } from './ShadowController.js';
import { IVector3 } from '../../../../api.js';
export class SpotLightShadowController extends ShadowController {
    constructor(_light, _babylon_light, _light_system) {
        const shadow_values = SHADOW_DEFAULTS.SPOT_LIGHT;
        super(_light, _babylon_light, _light_system, shadow_values.resolution, shadow_values.near, shadow_values.far, _babylon_light.getEngine().webGLVersion === 1 ? shadow_values.webgl1_bias : shadow_values.webgl2_bias);
    }
    // We override the function for enabling the shadows to be able to verify that our given cone angle is valid.
    set enable_shadows(value) {
        super.enable_shadows = value;
        this.validate_and_warn_shadow_angle();
    }
    // We need to override the getter as well, otherwise the setter above is not overriden properly.
    get enable_shadows() {
        return super.enable_shadows;
    }
    get_shadow_angle() {
        return this._light.shadow_angle_radians;
    }
    validate_and_warn_shadow_angle() {
        if (super.enable_shadows && this.get_shadow_angle() > SpotLightShadowController.MAX_SHADOW_ANGLE_RADIANS) {
            console.warn('SpotLightShadowController cone angle is out of range for rendering shadows. Expect broken shadows. Maximum value allowed: ' +
                degrees(SpotLightShadowController.MAX_SHADOW_ANGLE_RADIANS).toFixed(1) + ' degrees / ' + SpotLightShadowController.MAX_SHADOW_ANGLE_RADIANS.toFixed(2) + ' radians.\n' +
                'If using a custom shadow angle, set it before setting the inner/outer angle of the light to get rid of this warning.');
        }
    }
    should_update_shadowmap(meshes_modifications) {
        // TODO rework to `some`? compare performance
        // TODO refactor
        const light_direction = this._light.get_world_direction();
        const light_position = this._light.get_world_position();
        const shadow_cone_angle = this.get_shadow_angle();
        const light_cone_angle_sine = Math.sin(shadow_cone_angle * 0.5);
        const light_cone_angle_cosine = Math.cos(shadow_cone_angle * 0.5);
        for (const mod of meshes_modifications) {
            const rel_old = IVector3.subtract(mod.old_bounds.position, light_position); // old position relative to light
            const rel_new = IVector3.subtract(mod.old_bounds.position, light_position); // new position relative to light
            const old_dot_dir = IVector3.dot(rel_old, light_direction);
            const new_dot_dir = IVector3.dot(rel_new, light_direction);
            const old_radius = mod.old_bounds.radius;
            const new_radius = mod.new_bounds.radius;
            // TODO: When the radius hasn't changed we could save a bunch of work here.
            // If both the old and the new bounding sphere are outside the shadow near and far plane we can skip this modification.
            if ((old_dot_dir > this.shadow_far + old_radius || old_dot_dir < this.shadow_near - old_radius) &&
                (new_dot_dir > this.shadow_far + new_radius || new_dot_dir < this.shadow_near - new_radius))
                continue;
            const old_sphere_swept_cone_adj = old_radius / light_cone_angle_sine; // The amount to move the cone "backwards" to compensate for the radius of the sphere.
            const old_cone_vertex = IVector3.subtract(light_position, IVector3.scale(light_direction, old_sphere_swept_cone_adj));
            const old_cosa = IVector3.dot(light_direction, IVector3.normalize(IVector3.subtract(mod.old_bounds.position, old_cone_vertex)));
            if (old_cosa >= light_cone_angle_cosine) {
                // We are inside cone
                return true;
            }
            const new_sphere_swept_cone_adj = new_radius / light_cone_angle_sine; // The amount to move the cone "backwards" to compensate for the radius of the sphere.
            const new_cone_vertex = IVector3.subtract(light_position, IVector3.scale(light_direction, new_sphere_swept_cone_adj));
            const new_cosa = IVector3.dot(light_direction, IVector3.normalize(IVector3.subtract(mod.new_bounds.position, new_cone_vertex)));
            if (new_cosa >= light_cone_angle_cosine) {
                // We are inside cone
                return true;
            }
        }
        return false;
    }
}
// The maximum angle we consider is feasible for a spotlight shadow to render.
// At high angles, the shadow gets a lot of distortion and low quality. At 180 degrees, you get NaNs/INFs in the projection.
SpotLightShadowController.MAX_SHADOW_ANGLE_RADIANS = radians(170);
//# sourceMappingURL=SpotLightShadowController.js.map